import { RefreshRounded, Search } from "@mui/icons-material";
import {
    Alert,
    AlertTitle,
    Box,
    Container,
    IconButton,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxStatusCard from "components/UI/Cards/AcxStatusCard";
import { SagaKind } from "models/ISagaState";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import theme from "Theme/AppTheme";
import { extractGuid } from "utils/StringUtils";
import { useStore } from "utils/useStore";
import SagaGroupSection from "./SagaGroupSection";
import {
    getSagaTotals,
    groupSagasForDisplay,
    SagaGroup,
} from "./SagaInspectorData";
import SagaInspectorStore from "./SagaInspectorStore";

interface SagaSummaryCard {
    label: string;
    value: string;
    tooltipText: string;
    accentColor?: string;
}

function formatCheckedAt(value: Date) {
    return value.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
    });
}

const SagaInspector = observer(() => {
    const store = useStore(SagaInspectorStore);
    const [searchText, setSearchText] = useState("");
    const [submittedAmdId, setSubmittedAmdId] = useState("");
    const [searchError, setSearchError] = useState<string>();

    const submitSearch = () => {
        const normalizedAmdId = extractGuid(searchText);
        if (!normalizedAmdId) {
            setSearchError("Enter a valid AMD ID.");
            setSubmittedAmdId("");
            return;
        }

        setSearchError(undefined);
        setSubmittedAmdId(normalizedAmdId);
    };

    useEffect(() => {
        if (!submittedAmdId) {
            store.reset();
            return;
        }

        void store.getAllSagasState(submittedAmdId, true);
    }, [store, submittedAmdId]);

    const refresh = React.useCallback(() => {
        if (!submittedAmdId) return;
        void store.getAllSagasState(submittedAmdId, false);
    }, [store, submittedAmdId]);

    const sagas = store.sagas;
    const totals = React.useMemo(
        () => (sagas ? getSagaTotals(sagas) : undefined),
        [sagas],
    );
    const sagaGroups = React.useMemo(
        () => (sagas ? groupSagasForDisplay(sagas) : []),
        [sagas],
    );

    // Only groups the user has explicitly toggled are tracked; everything else
    // follows the default below, so a refresh that changes a group's completion
    // does not fight a choice the user made.
    const [groupOverrides, setGroupOverrides] = useState<
        Partial<Record<SagaKind, boolean>>
    >({});

    React.useEffect(() => {
        setGroupOverrides({});
    }, [submittedAmdId]);

    const isGroupExpanded = (group: SagaGroup) =>
        groupOverrides[group.kind] ??
        group.totals.complete !== group.totals.total;

    const toggleGroup = (group: SagaGroup) =>
        setGroupOverrides((current) => ({
            ...current,
            [group.kind]: !isGroupExpanded(group),
        }));

    const displayLoading = !sagas && store.isLoading;
    const hasSagas = (sagas?.length ?? 0) > 0;
    const allComplete = hasSagas && totals?.total === totals?.complete;

    const summaryCards: SagaSummaryCard[] = totals
        ? [
              {
                  label: "Total Sagas",
                  value: `${totals.total}`,
                  tooltipText: "Sagas recorded for this AMD.",
              },
              {
                  label: "Complete",
                  value: `${totals.complete}`,
                  tooltipText:
                      "Sagas whose requirements have all been satisfied.",
                  accentColor: theme.palette.success.main,
              },
              {
                  label: "In Progress",
                  value: `${totals.inProgress}`,
                  tooltipText:
                      "Sagas still waiting on one or more requirements.",
                  accentColor: theme.palette.warning.main,
              },
          ]
        : [];

    const endAdornment = (
        <IconButton
            aria-label="Search for AMD saga state"
            size="small"
            onClick={submitSearch}
        >
            <Search fontSize="small" />
        </IconButton>
    );

    return (
        <Paper
            style={{
                height: "100%",
                overflow: "auto",
                background: theme.palette.lightgray.main,
                position: "relative",
            }}
        >
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        minHeight: "70px",
                        alignItems: "center",
                        background: "white",
                        boxShadow:
                            "0 0 3px 0 rgb(0 0 0 / 5%), 0 1px 1px 0 rgb(0 0 0 / 5%)",
                    }}
                >
                    <div style={{ marginLeft: "12px", marginRight: "auto" }}>
                        <Typography variant="h5">
                            Saga State Inspector
                        </Typography>
                    </div>
                    <div style={{ marginLeft: "auto", marginRight: "12px" }}>
                        <AcxMainTextField
                            id="amdSearch"
                            placeholderText="Show Sagas by AMD ID"
                            type="text"
                            containerStyle={{
                                width: "clamp(320px, 35vw, 480px)",
                            }}
                            fullWidth
                            value={searchText}
                            onChange={(e) => {
                                const value = e.target.value;
                                setSearchText(value);
                                setSearchError(undefined);
                                setSubmittedAmdId(extractGuid(value) ?? "");
                            }}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") submitSearch();
                            }}
                            error={Boolean(searchError)}
                            errorHelper={searchError}
                            helperText={searchError}
                            endAdornment={endAdornment}
                        />
                    </div>
                </div>

                <Container maxWidth="xl">
                    {store.lastCheckedAt && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="flex-end"
                            spacing={2}
                            sx={{ my: 2 }}
                        >
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Last checked at{" "}
                                {formatCheckedAt(store.lastCheckedAt)}
                            </Typography>
                            <AcxButton
                                color="white"
                                startIcon={<RefreshRounded />}
                                loading={store.isLoading}
                                disabledTooltip="Refreshing saga state"
                                onClick={refresh}
                            >
                                {store.isLoading ? "Refreshing" : "Refresh"}
                            </AcxButton>
                        </Stack>
                    )}

                    {store.loadError && (
                        <Alert
                            severity="error"
                            sx={{ my: 2, borderRadius: 2 }}
                            action={
                                <AcxButton
                                    color="white"
                                    loading={store.isLoading}
                                    onClick={refresh}
                                >
                                    Try again
                                </AcxButton>
                            }
                        >
                            <AlertTitle>{store.loadError.title}</AlertTitle>
                            {store.loadError.message}
                            {sagas && " The existing state may be out of date."}
                        </Alert>
                    )}

                    {displayLoading && (
                        <AcxLoadingIndicator
                            size={64}
                            alternate="PuffLoader"
                        />
                    )}

                    {totals && hasSagas && (
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "minmax(0, 1fr)",
                                    md: "repeat(3, minmax(0, 1fr))",
                                },
                                gap: 2,
                                mb: 3,
                            }}
                        >
                            {summaryCards.map(
                                ({
                                    label,
                                    value,
                                    tooltipText,
                                    accentColor,
                                }) => (
                                    <AcxStatusCard
                                        key={label}
                                        caption={label}
                                        captionVariant="caption"
                                        tooltipText={tooltipText}
                                        containerStyles={{
                                            minWidth: 0,
                                            border: `1px solid ${theme.palette.gray[200]}`,
                                            borderRadius:
                                                theme.shape.borderRadius * 2,
                                            boxShadow: "none",
                                        }}
                                        primaryContent={
                                            <Stack sx={{ width: "100%" }}>
                                                <Typography
                                                    variant="h1"
                                                    sx={{
                                                        mt: 0.25,
                                                        color: "text.secondary",
                                                        fontFamily:
                                                            theme.typography
                                                                .fontFamily,
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {value}
                                                </Typography>
                                                {accentColor && (
                                                    <Box
                                                        aria-hidden
                                                        sx={{
                                                            mt: 1,
                                                            height: 5,
                                                            borderRadius: 2,
                                                            backgroundColor:
                                                                accentColor,
                                                        }}
                                                    />
                                                )}
                                            </Stack>
                                        }
                                    />
                                ),
                            )}
                        </Box>
                    )}

                    {sagas && !hasSagas && (
                        <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
                            <AlertTitle>No saga records found</AlertTitle>
                            No sagas have been recorded for this AMD. If the
                            file is still early in the pipeline, wait a moment
                            and refresh.
                        </Alert>
                    )}

                    {allComplete && (
                        <Alert
                            severity="success"
                            sx={{ mb: 3, borderRadius: 2 }}
                        >
                            <AlertTitle>All sagas complete</AlertTitle>
                            Every saga for this AMD has satisfied its
                            requirements.
                        </Alert>
                    )}

                    {hasSagas && (
                        <Stack spacing={3} sx={{ pb: 3 }}>
                            {sagaGroups.map((group) => (
                                <SagaGroupSection
                                    key={group.kind}
                                    group={group}
                                    expanded={isGroupExpanded(group)}
                                    onToggle={() => toggleGroup(group)}
                                />
                            ))}
                        </Stack>
                    )}
                </Container>
            </div>
        </Paper>
    );
});

export default SagaInspector;
